import React, { useMemo } from 'react';
import { ArrowDown, ArrowUp, Check, Plus, Trash2, X } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import {
  coverage, describeBox, outcomesOf, outcomeLabel, routesByQuestion, isRange, topOf,
} from './routing';
import { fieldClass, addButtonClass } from './QuestionsEditor';

/**
 * RouteTable — where a decision step sends each combination of answers.
 *
 * Routes are tried top to bottom; the first whose conditions all hold wins.
 * A condition is "question is one of these outcomes"; a question without a
 * condition matches anything. Otherwise takes the rest. Coverage is exact
 * (routing.coverage), so a gap is listed, never guessed.
 *
 * value: { routes: [{ id, name, when: { key: [outcome] }, to? }], otherwise: { name, to? } | null }
 * questions: the API map ({ key: { type, criteria } })
 *
 * renderTarget(route | 'otherwise') → a "goes to" picker for that route
 * (evals: the next step). otherwiseRequired: Otherwise always exists (evals:
 * a step must have a default edge).
 */

let seq = 0;
const newId = (taken) => {
  for (;;) {
    seq += 1;
    const id = `route_${seq}`;
    if (!taken.includes(id)) return id;
  }
};

const iconButton = 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] disabled:opacity-30';
const chip = (on) => `inline-flex h-6 items-center gap-1 rounded-md border px-2 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] ${on
  ? 'border-[color:var(--trodo-primary)] bg-[color:var(--trodo-primary)] text-[color:var(--text-on-brand)]'
  : 'border-[color:var(--border-default)] bg-surface text-[color:var(--text-secondary)] hover:bg-surface-hover'}`;
const pickerTrigger = '!h-7 !px-2 !py-0 !bg-transparent !text-[12px]';

/** A dropdown that acts once and resets: "Add condition", "Split by". */
function ActionPicker({ label, options, onPick }) {
  return (
    <BrandDropdown
      value={null}
      options={options}
      placeholder={label}
      ariaLabel={label}
      compact
      triggerClassName={pickerTrigger}
      onChange={(v) => { if (v) onPick(v); }}
    />
  );
}

function Condition({ qKey, q, allowed, onChange, onRemove, readOnly }) {
  const all = outcomesOf(q);
  const toggle = (o) => {
    const next = allowed.includes(o) ? allowed.filter((x) => x !== o) : [...allowed, o];
    onChange(all.filter((x) => next.includes(x)));
  };
  return (
    <div className="flex items-start gap-2" data-testid="route-condition">
      <span className="mt-1 w-[120px] shrink-0 truncate font-mono text-[12px] text-[color:var(--text-primary)]" title={qKey}>{qKey}</span>
      <div className="flex min-w-0 flex-1 flex-wrap gap-1">
        {all.map((o) => (
          <button
            key={o}
            type="button"
            className={chip(allowed.includes(o))}
            aria-pressed={allowed.includes(o)}
            disabled={readOnly}
            onClick={() => toggle(o)}
            title={outcomeLabel(q, o)}
          >
            {allowed.includes(o) && <Check className="h-3 w-3" aria-hidden />}
            <span className="max-w-[160px] truncate">{q.type === 'score' && o !== 'cant_tell' ? o : outcomeLabel(q, o)}</span>
          </button>
        ))}
      </div>
      {!readOnly && (
        <button type="button" className={iconButton} aria-label={`Remove condition on ${qKey}`} onClick={onRemove}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

const numInput = `h-6 w-[56px] ${fieldClass}`;

/** A score condition: min ≤ value < max (≤ at the top of the scale). */
function RangeCondition({ qKey, q, value, onChange, onRemove, readOnly }) {
  const top = topOf(q);
  const r = value && !Array.isArray(value) ? value : { min: 0, max: top };
  const set = (patch) => onChange({ ...r, ...patch });
  const num = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  return (
    <div className="flex items-center gap-2 text-[12px]" data-testid="route-condition">
      <span className="w-[96px] shrink-0 truncate font-mono text-[color:var(--text-primary)]" title={qKey}>{qKey}</span>
      <span className="text-[color:var(--text-muted)]">≥</span>
      <input type="number" step="0.1" min={0} max={top} value={r.min} disabled={readOnly} aria-label={`${qKey} from`} className={numInput}
        onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) set({ min: num(v, 0, top) }); }} />
      <span className="text-[color:var(--text-muted)]">{r.max >= top ? '≤' : '<'}</span>
      <input type="number" step="0.1" min={0} max={top} value={r.max} disabled={readOnly} aria-label={`${qKey} to`} className={numInput}
        onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) set({ max: num(v, 0, top) }); }} />
      <span className="whitespace-nowrap text-[color:var(--text-muted)]">0–{top}</span>
      {!readOnly && (
        <button type="button" className={`${iconButton} ml-auto`} aria-label={`Remove condition on ${qKey}`} onClick={onRemove}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function RouteCard({
  route, index, count, questions, status, onChange, onRemove, onMove, onSplit, readOnly, target,
}) {
  const when = route.when || {};
  const used = Object.keys(when).filter((k) => questions[k]);
  const free = Object.keys(questions).filter((k) => !used.includes(k)).map((k) => ({ value: k, label: k, monospace: true }));
  const setWhen = (k, allowed) => {
    const next = { ...when };
    if (allowed == null) delete next[k];
    else next[k] = allowed;
    onChange({ ...route, when: next });
  };
  return (
    <div
      className={`rounded-lg border border-[color:var(--border-default)] bg-surface ${status && status.never ? 'opacity-60' : ''}`}
      data-testid="route-card"
    >
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <span className="w-5 shrink-0 text-center text-[11px] tabular-nums text-[color:var(--text-muted)]">{index + 1}</span>
        <input
          className={`h-7 min-w-0 flex-1 ${fieldClass}`}
          value={route.name}
          disabled={readOnly}
          aria-label="Route name"
          placeholder="Route name"
          onChange={(e) => onChange({ ...route, name: e.target.value })}
        />
        {!readOnly && (
          <>
            <button type="button" className={iconButton} aria-label="Move route up" disabled={index === 0} onClick={() => onMove(-1)}><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" className={iconButton} aria-label="Move route down" disabled={index === count - 1} onClick={() => onMove(1)}><ArrowDown className="h-3.5 w-3.5" /></button>
            <button type="button" className={iconButton} aria-label={`Delete route ${route.name || index + 1}`} onClick={onRemove}><Trash2 className="h-3.5 w-3.5" /></button>
          </>
        )}
      </div>
      <div className="space-y-1.5 border-t border-[color:var(--border-default)] px-3 py-2">
        {used.map((k) => (isRange(questions[k]) ? (
          <RangeCondition
            key={k}
            qKey={k}
            q={questions[k]}
            value={when[k]}
            readOnly={readOnly}
            onChange={(r) => setWhen(k, r)}
            onRemove={() => setWhen(k, null)}
          />
        ) : (
          <Condition
            key={k}
            qKey={k}
            q={questions[k]}
            allowed={Array.isArray(when[k]) ? when[k] : []}
            readOnly={readOnly}
            onChange={(allowed) => setWhen(k, allowed)}
            onRemove={() => setWhen(k, null)}
          />
        )))}
        {!readOnly && free.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            <ActionPicker label="Add condition" options={free} onPick={(k) => setWhen(k, isRange(questions[k]) ? { min: 0, max: topOf(questions[k]) } : [])} />
            <ActionPicker label="Split by" options={free} onPick={onSplit} />
          </div>
        )}
        {target && <div className="flex items-center gap-2 pt-0.5"><span className="text-[12px] text-[color:var(--text-muted)]">Goes to</span>{target}</div>}
        {status && status.empty && (
          <p className="text-[12px] text-[color:var(--feedback-error)]">Matches no answer.</p>
        )}
        {status && status.never && !status.empty && (
          <p className="text-[12px] text-[color:var(--text-muted)]">Never reached</p>
        )}
      </div>
    </div>
  );
}

export default function RouteTable({
  questions, value, onChange, readOnly = false, renderTarget = null, otherwiseRequired = false,
}) {
  const table = value || { routes: [], otherwise: { name: 'Otherwise' } };
  const routes = table.routes || [];
  const hasQuestions = Object.keys(questions || {}).length > 0;
  const otherwiseOn = otherwiseRequired || !!table.otherwise;
  const effective = otherwiseOn ? { ...table, otherwise: table.otherwise || { name: 'Otherwise' } } : table;
  const cov = useMemo(() => (hasQuestions ? coverage(questions, effective) : null), [questions, effective, hasQuestions]);
  const set = (patch) => onChange({ ...table, ...patch });
  const setRoutes = (next) => set({ routes: next });
  const taken = routes.map((r) => r.id);
  const keyOptions = Object.keys(questions || {}).map((k) => ({ value: k, label: k, monospace: true }));

  const split = (i, key) => {
    const base = routes[i];
    const used = taken.slice();
    const pieces = routesByQuestion(questions, key).map((p) => {
      const id = newId(used);
      used.push(id);
      return { ...base, id, name: `${base.name || 'Route'} · ${p.name}`, when: { ...(base.when || {}), ...p.when } };
    });
    setRoutes([...routes.slice(0, i), ...pieces, ...routes.slice(i + 1)]);
  };

  if (!hasQuestions) {
    return <p className="text-[12px] text-[color:var(--text-muted)]">Add a question first.</p>;
  }

  return (
    <div className="space-y-2" data-testid="route-table">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <ActionPicker
            label="Route by one question"
            options={keyOptions}
            onPick={(k) => setRoutes(routesByQuestion(questions, k).map((r) => ({ ...r, id: r.id.slice(0, 60) })))}
          />
          <button
            type="button"
            className={addButtonClass}
            onClick={() => setRoutes([...routes, { id: newId(taken), name: `Route ${routes.length + 1}`, when: {} }])}
          >
            <Plus className="h-3.5 w-3.5" /> Route
          </button>
        </div>
      )}

      {routes.map((r, i) => (
        <RouteCard
          key={r.id}
          route={r}
          index={i}
          count={routes.length}
          questions={questions}
          status={cov && cov.rows[i]}
          readOnly={readOnly}
          target={renderTarget ? renderTarget(r, i) : null}
          onChange={(next) => setRoutes(routes.map((x, j) => (j === i ? next : x)))}
          onRemove={() => setRoutes(routes.filter((_, j) => j !== i))}
          onMove={(d) => {
            const next = routes.slice();
            const [m] = next.splice(i, 1);
            next.splice(i + d, 0, m);
            setRoutes(next);
          }}
          onSplit={(k) => split(i, k)}
        />
      ))}

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[color:var(--border-default)] bg-surface px-3 py-2" data-testid="route-otherwise">
        {otherwiseRequired ? (
          <span className="text-[12px] font-medium text-[color:var(--text-primary)]">Otherwise</span>
        ) : (
          <label className="flex items-center gap-2 text-[12px] font-medium text-[color:var(--text-primary)]">
            <input
              type="checkbox"
              checked={!!table.otherwise}
              disabled={readOnly}
              onChange={(e) => set({ otherwise: e.target.checked ? { ...(table.otherwise || {}), name: (table.otherwise && table.otherwise.name) || 'Otherwise' } : null })}
            />
            Otherwise
          </label>
        )}
        {otherwiseOn && renderTarget && (
          <span className="ml-auto flex items-center gap-2"><span className="text-[12px] text-[color:var(--text-muted)]">Goes to</span>{renderTarget('otherwise')}</span>
        )}
      </div>

      {cov && !cov.complete && (
        <div className="rounded-lg border border-[color:var(--feedback-error)] px-3 py-2 text-[12px]" data-testid="route-coverage" role="alert">
          <p className="font-medium text-[color:var(--feedback-error)]">
            {cov.uncovered_count == null
              ? 'Some answers go nowhere.'
              : `${cov.uncovered_count.toLocaleString()} of ${cov.total.toLocaleString()} combinations go nowhere.`}
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[color:var(--text-secondary)]">
            {cov.uncovered.slice(0, 6).map((b) => <li key={describeBox(questions, b)}>{describeBox(questions, b)}</li>)}
            {cov.uncovered.length > 6 && <li>and {cov.uncovered.length - 6} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
